import { ability, ActionMap } from '@oliasoft/authorization-ui';
import { IEstimate } from 'client/common/interfaces/hierarchy.interfaces';
import { CaslEnums } from 'src/enums/casl-enums';
import { AccessTypes, SelectedUsersTypes } from 'src/enums/access-control';

/**
 * Validates if a user has access to an estimate based on various access control rules
 *
 * @param currentUserId - ID of the user attempting to access the estimate
 * @param estimate - The estimate object to check access for
 * @returns boolean indicating whether the user has access
 */
export const validateEstimateAccess = (currentUserId: string, estimate: IEstimate): boolean => {
  const hasAdminAccess = ability.can(ActionMap.UPDATE, CaslEnums.ManageAccessRights);
  if (!estimate.accessRights) {
    return true;
  }

  const { accessMetadata } = estimate;
  const isEstimateOwner = currentUserId === accessMetadata?.restrictedby;
  const isPublicAccess = accessMetadata?.accesstype === AccessTypes.All;

  if (hasAdminAccess || isEstimateOwner || isPublicAccess) {
    return true;
  }

  const isUserInAccessList = !!estimate.accessRights.find((r) => r.subjectid === currentUserId);

  switch (estimate.selectedUsersType) {
    case SelectedUsersTypes.Allow:
      return isUserInAccessList;
    case SelectedUsersTypes.Block:
      return !isUserInAccessList;
    default:
      return false;
  }
};

/**
 * Checks if an estimate has restricted access based on its access type and user list.
 * An estimate is considered restricted if:
 * - It has an access type of 'OnlyMe' or 'Selected'
 * - It has one or more users in its access list
 *
 * @param estimate - The estimate to check for access restrictions
 * @returns True if the estimate has restricted access, false otherwise
 */
export const hasRestrictedAccess = (estimate: IEstimate): boolean => {
  const restrictedAccessTypes: AccessTypes[] = [AccessTypes.OnlyMe, AccessTypes.Selected];

  const hasRestrictedAccessType = restrictedAccessTypes.includes(estimate.accessMetadata?.accesstype as AccessTypes);
  const hasAccessUsersList = Boolean(estimate.accessRights?.length);

  return hasRestrictedAccessType || hasAccessUsersList;
};
